import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface LocationPickerProps {
  latitude: number;
  longitude: number;
  onLocationChange: (lat: number, lon: number) => void;
}

/** Geocode an address using Nominatim (OSM) */
async function geocodeAddress(
  query: string
): Promise<{ lat: number; lon: number; display: string } | null> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`,
      { headers: { 'Accept-Language': 'pl' } }
    );
    const results = await response.json();
    if (results.length > 0) {
      return {
        lat: parseFloat(results[0].lat),
        lon: parseFloat(results[0].lon),
        display: results[0].display_name,
      };
    }
  } catch (e) {
    console.error('Geocoding error:', e);
  }
  return null;
}

/** Component that handles click events on the map */
function MapClickHandler({
  onLocationChange,
}: {
  onLocationChange: (lat: number, lon: number) => void;
}) {
  useMapEvents({
    click(e) {
      onLocationChange(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

/** Component that flies the map to a new position */
function FlyTo({ lat, lon }: { lat: number; lon: number }) {
  const map = useMap();
  const prevRef = useRef({ lat, lon });

  useEffect(() => {
    if (prevRef.current.lat !== lat || prevRef.current.lon !== lon) {
      map.flyTo([lat, lon], 15, { duration: 1 });
      prevRef.current = { lat, lon };
    }
  }, [lat, lon, map]);

  return null;
}

const LocationPicker: React.FC<LocationPickerProps> = ({
  latitude,
  longitude,
  onLocationChange,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    setSearchError('');

    const result = await geocodeAddress(searchQuery);
    if (result) {
      onLocationChange(result.lat, result.lon);
    } else {
      setSearchError(t('components.locNotFound'));
    }

    setSearching(false);
  };

  const center: [number, number] = [latitude || 52.23, longitude || 21.01];

  return (
    <div className="space-y-3">
      {/* Search bar */}
      <div className="flex gap-2">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSearch();
            }
          }}
          placeholder={t('components.locSearchPlaceholder')}
          className="flex-1 rounded-md border border-gray-300 shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          disabled={searching}
        />
        <button
          type="button"
          onClick={handleSearch}
          disabled={searching || !searchQuery.trim()}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {searching ? t('components.locSearching') : t('components.locSearch')}
        </button>
      </div>
      {searchError && <p className="text-sm text-red-600">{searchError}</p>}

      {/* Map */}
      <div className="h-64 rounded-lg overflow-hidden border border-gray-300">
        <MapContainer
          center={center}
          zoom={latitude ? 15 : 6}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onLocationChange={onLocationChange} />
          <FlyTo lat={center[0]} lon={center[1]} />
          {(latitude !== 0 || longitude !== 0) && <Marker position={[latitude, longitude]} />}
        </MapContainer>
      </div>

      <p className="text-xs text-gray-500">{t('components.locClickHint')}</p>
    </div>
  );
};

export default LocationPicker;
